

export const intersect = <T=any>(a: T[], b: T[]) => {
  var setB = new Set(b);
  return [...new Set(a)].filter(x => setB.has(x));
};

export const hasShadowDom = (el: HTMLElement) => {
  return !!el.shadowRoot && !!(el as any).attachShadow;
};

export type Attributes = { [key: string]: any };

export const inheritAttributes = (el: HTMLElement, attributes: string[] = []) => {
  const attributeObject: Attributes = {};

  attributes.forEach((attr) => {
    if (el.hasAttribute(attr)) {
      const value = el.getAttribute(attr);
      if (value !== null) {
        attributeObject[attr] = el.getAttribute(attr);
      }
      el.removeAttribute(attr);
    }
  });

  return attributeObject;
};

const ariaAttributes = [
  'role',
  'aria-activedescendant',
  'aria-atomic',
  'aria-autocomplete',
  'aria-braillelabel',
  'aria-brailleroledescription',
  'aria-busy',
  'aria-checked',
  'aria-colcount',
  'aria-colindex',
  'aria-colindextext',
  'aria-colspan',
  'aria-controls',
  'aria-current',
  'aria-describedby',
  'aria-description',
  'aria-details',
  'aria-disabled',
  'aria-errormessage',
  'aria-expanded',
  'aria-flowto',
  'aria-haspopup',
  'aria-hidden',
  'aria-invalid',
  'aria-keyshortcuts',
  'aria-label',
  'aria-labelledby',
  'aria-level',
  'aria-live',
  'aria-multiline',
  'aria-multiselectable',
  'aria-orientation',
  'aria-owns',
  'aria-placeholder',
  'aria-posinset',
  'aria-pressed',
  'aria-readonly',
  'aria-relevant',
  'aria-required',
  'aria-roledescription',
  'aria-rowcount',
  'aria-rowindex',
  'aria-rowindextext',
  'aria-rowspan',
  'aria-selected',
  'aria-setsize',
  'aria-sort',
  'aria-valuemax',
  'aria-valuemin',
  'aria-valuenow',
  'aria-valuetext',
];

export const inheritAriaAttributes = (el: HTMLElement, ignoreList?: string[]) => {
  let attributesToInherit = ariaAttributes;
  if (ignoreList && ignoreList.length > 0) {
    attributesToInherit = attributesToInherit.filter((attr) => !ignoreList.includes(attr));
  }
  return inheritAttributes(el, attributesToInherit);
};

export const hostContext = (selector: string, el: HTMLElement): boolean => {
  return el.closest(selector) !== null;
}

export const renderHiddenInput = (always: boolean, container: HTMLElement, name: string, value: string | undefined | null, disabled: boolean, required?: boolean) => {
  if (always || hasShadowDom(container)) {
    let input = container.querySelector(':scope > input.aux-input') as HTMLInputElement | null;

    if (!input) {
      input = container.ownerDocument!.createElement('input');
      input.style.opacity = '0';
      input.style.position = 'absolute';
      input.style.left = '0';
      input.style.top = '0';
      input.style.width = '0px';
      input.style.height = '0px';
      input.style.margin = '0';
      input.style.padding = '0';
      input.classList.add('aux-input');
      input.tabIndex = -1;
      container.appendChild(input);
    }

    input.inputMode = 'none';
    input.disabled = disabled;
    input.name = name;
    input.value = value || '';
    input.required = required;
  }
};

//check if element has siblings of same type
export const hasSiblingsOfSameType = (el: HTMLElement) => {
  if (!el.parentNode) {
    return false;
  }
  const parent = el.parentNode as HTMLElement;
  const siblings = Array.from(parent.children).filter(child => child !== el);
  const elType = el.tagName.toLowerCase();

  return siblings.some(sibling => sibling.tagName.toLowerCase() === elType);
}


export const mergeFilters = (obj1: any, obj2: any) => {
  return {
    ...removeUndefinedProps(obj1),
    ...removeUndefinedProps(obj2)
  }
}

const removeUndefinedProps = (obj: any) => {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key] !== undefined && obj[key] !== null) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

export const createHiddenFormInput = (container: HTMLElement, name: string = '', value: any = null, disabled: boolean = false, required: boolean = false): HTMLInputElement | undefined => {


  const input = document.createElement('input');
  input.name = name;
  input.value = value;
  input.disabled = disabled || null;
  input.required = required || null;
  input.style.opacity = '0';
  input.style.position = 'absolute';
  input.style.left = '50%';
  input.style.bottom = '0';
  input.style.width = '0px';
  input.style.height = '0px';
  input.style.float = 'left';
  input.style.margin = '0';
  input.style.padding = '0';

  container.appendChild(input);


  return input;
}

export const kebabize = (str) => str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? "-" : "") + $.toLowerCase());


export const confirmEvent = (event: Event, message: string) => {

  const confirmed = confirm(message);

  if (!confirmed) {
    event.preventDefault();
  }

}

export const getAriaLabel = (
  componentEl: HTMLElement,
  inputId: string
): { label: Element | null; labelId: string; labelText: string | null | undefined } => {
  let labelText;

  // If the user provides their own label via the aria-labelledby attr
  // we should use that instead of looking for an ion-label
  const labelledBy = componentEl.getAttribute('aria-labelledby');

  // Grab the id off of the component in case they are using
  // a custom label using the label element
  const componentId = componentEl.id;

  let labelId = labelledBy !== null && labelledBy.trim() !== '' ? labelledBy : inputId + '-lbl';

  let label =
    labelledBy !== null && labelledBy.trim() !== '' ? document.getElementById(labelledBy) : null;

  if (label) {
    if (labelledBy === null) {
      label.id = labelId;
    }

    labelText = label.textContent;
    label.setAttribute('aria-hidden', 'true');

  } else if (componentId.trim() !== '') {
    label = document.querySelector(`label[for="${componentId}"]`);

    if (label) {
      if (label.id !== '') {
        labelId = label.id;
      } else {
        label.id = labelId = `${componentId}-lbl`;
      }

      labelText = label.textContent;
    }
  }

  return { label, labelId, labelText };
};
